import type { apiClient } from '~/shared/api/data-ingestion-api'
import type {
  AuditTrailEventDescriptionField,
  CheckedPatientData,
  EobDuplicationEventProps,
} from '../../model'
import type { EobAuditEvent } from '../eob-types'
import { asEobEvent, findPreviousPaymentSummaryEvent } from '../eob-types'
import { collectFieldSearchText } from './searchable-text-utils'

/** Check if there is a previous eob_duplication event (for title logic only). */
function hasPreviousDuplicationEvent(
  filteredEvents: apiClient.AuditEvent[],
  index: number,
): boolean {
  for (let i = index - 1; i >= 0; i--) {
    const e = filteredEvents[i]
    if (e.event_type === 'eob_duplication') {
      const eob = asEobEvent(e)
      if (eob.payment_summary) return true
    }
  }
  return false
}

// ---------------------------------------------------------------------------
// Checked patients helpers
// ---------------------------------------------------------------------------

const formatString = (v: unknown): string | null => (typeof v === 'string' && v ? v : null)

interface PatientFieldConfig {
  key: keyof apiClient.CheckedPatient
  label: string
  format: (value: unknown) => string | null
}

const PATIENT_FIELDS: PatientFieldConfig[] = [
  { key: 'patient_account_number', label: 'Patient Account Number', format: formatString },
  { key: 'patient_first_name', label: 'Patient First Name', format: formatString },
  { key: 'patient_last_name', label: 'Patient Last Name', format: formatString },
]

function buildCheckedPatients(
  current: EobAuditEvent,
  previous: EobAuditEvent | null,
): CheckedPatientData[] | null {
  const currentPatients = current.payment_summary?.checked_patients
  if (!currentPatients || currentPatients.length === 0) return null

  const previousPatients = previous?.payment_summary?.checked_patients ?? []

  const results: CheckedPatientData[] = []

  for (let i = 0; i < currentPatients.length; i++) {
    const patient = currentPatients[i]
    const prevPatient = previousPatients[i]
    const fields: AuditTrailEventDescriptionField[] = []

    for (const config of PATIENT_FIELDS) {
      const currentFormatted = config.format(patient[config.key])

      if (!prevPatient) {
        if (currentFormatted !== null) {
          fields.push({
            label: config.label,
            values: [{ diff: [currentFormatted] }],
          })
        }
      } else {
        const prevFormatted = config.format(prevPatient[config.key])
        if (currentFormatted !== prevFormatted) {
          fields.push({
            label: config.label,
            values: [
              {
                previous: prevFormatted ?? '--',
                current: currentFormatted ?? '--',
              },
            ],
          })
        }
      }
    }

    if (fields.length > 0) {
      results.push({ patientIndex: i + 1, fields })
    }
  }

  return results.length > 0 ? results : null
}

// ---------------------------------------------------------------------------
// Title helper
// ---------------------------------------------------------------------------

function getDuplicationTitle(eobEvent: EobAuditEvent, isRevalidation: boolean): string {
  const noDuplicates = eobEvent.payment_summary?.no_duplicates_in_amd
  const statusLabel =
    noDuplicates === true
      ? 'Not Posted Yet'
      : noDuplicates === false
        ? 'Duplicate (Already in AMD)'
        : 'Status Unknown'

  if (!isRevalidation) {
    return `Duplication: Fields Validated – ${statusLabel}`
  }
  return `Duplication: Fields Revalidated – ${statusLabel}`
}

// ---------------------------------------------------------------------------
// Main transform
// ---------------------------------------------------------------------------

export function transformEobDuplication(
  event: apiClient.AuditEvent,
  filteredEvents: apiClient.AuditEvent[],
  index: number,
): EobDuplicationEventProps {
  const eobEvent = asEobEvent(event)
  const previousEvent = findPreviousPaymentSummaryEvent(filteredEvents, index)
  const isRevalidation = hasPreviousDuplicationEvent(filteredEvents, index)

  const title = getDuplicationTitle(eobEvent, isRevalidation)
  const patients = buildCheckedPatients(eobEvent, previousEvent)
  const notes = event.notes ?? null

  return {
    type: 'eob-duplication',
    timestamp: event.timestamp,
    user: event.user,
    userRole: event.user ? 'person' : 'opti',
    step: 'eob-duplication',
    eventType: event.event_type,
    title,
    patients,
    notes,
  }
}

export function getEobDuplicationSearchableText(props: EobDuplicationEventProps): string {
  const parts = [props.title]
  if (props.patients) {
    for (const patient of props.patients) {
      if (patient.fields.length > 0) parts.push(collectFieldSearchText(patient.fields))
    }
  }
  if (props.notes) parts.push(props.notes)
  return parts.join(' ')
}
